/**
 * 用户主题实例管理的数据请求（旧 composables/useUserInstances.ts、useEpisodes.ts）
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { API_ENDPOINTS } from '@/api/endpoints'
import type { InstanceAction, InstanceEpisode, InstanceListQuery, InstanceStats, UserInstance } from './types'

const INSTANCES_KEY = ['admin', 'user-instances'] as const

export function useInstanceList(query: InstanceListQuery) {
  return useQuery({
    queryKey: [...INSTANCES_KEY, 'list', query],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.USER_INSTANCES.LIST, {
        params: {
          skip: (query.page - 1) * query.pageSize,
          limit: query.pageSize,
          keyword: query.keyword || undefined,
          theme_id: query.themeId,
          status: query.status
        }
      })
      return {
        instances: (response.data.instances ?? []) as UserInstance[],
        total: (response.data.total ?? 0) as number
      }
    },
    placeholderData: keepPreviousData
  })
}

// 主题筛选下拉框的选项；失败时只打印，不影响列表
export function useThemeOptions() {
  return useQuery({
    queryKey: [...INSTANCES_KEY, 'theme-options'],
    queryFn: async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.THEMES.LIST, { params: { limit: 100 } })
        return ((response.data.themes ?? []) as { id: number; title: string }[]).map((theme) => ({
          value: theme.id,
          label: theme.title
        }))
      } catch (error) {
        console.error('获取主题列表失败', error)
        return []
      }
    }
  })
}

// 详情先用列表行数据显示，内容更新后重新请求详情
export function useInstanceDetail(instance: UserInstance | null) {
  return useQuery({
    queryKey: [...INSTANCES_KEY, 'detail', instance?.id],
    enabled: instance !== null,
    initialData: instance ?? undefined,
    staleTime: Infinity,
    queryFn: async () => (await apiClient.get(API_ENDPOINTS.USER_INSTANCES.DETAIL((instance as UserInstance).id))).data as UserInstance
  })
}

// 篇章与统计按实例 id 缓存，打开另一个实例时不会显示上一个实例的数据
export function useInstanceEpisodes(instanceId: number | null) {
  return useQuery({
    queryKey: [...INSTANCES_KEY, 'episodes', instanceId],
    enabled: instanceId !== null,
    queryFn: async () =>
      ((await apiClient.get(API_ENDPOINTS.USER_INSTANCES.EPISODES(instanceId as number))).data ?? []) as InstanceEpisode[]
  })
}

export function useInstanceStats(instanceId: number | null) {
  return useQuery({
    queryKey: [...INSTANCES_KEY, 'stats', instanceId],
    enabled: instanceId !== null,
    queryFn: async () => (await apiClient.get(API_ENDPOINTS.USER_INSTANCES.STATS(instanceId as number))).data as InstanceStats
  })
}

const ACTION_ENDPOINTS: Record<InstanceAction, (id: number) => string> = {
  pause: API_ENDPOINTS.USER_INSTANCES.PAUSE,
  resume: API_ENDPOINTS.USER_INSTANCES.RESUME,
  complete: API_ENDPOINTS.USER_INSTANCES.COMPLETE
}

export function useInstanceAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: InstanceAction }) => apiClient.post(ACTION_ENDPOINTS[action](id)),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INSTANCES_KEY })
  })
}

export function useDeleteInstance() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiClient.delete(API_ENDPOINTS.USER_INSTANCES.DETAIL(id)),
    // 删除实例会连带删除篇章，不自动重试
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INSTANCES_KEY })
  })
}

export function useUpdateInstanceContent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      apiClient.put(API_ENDPOINTS.USER_INSTANCES.CONTENT(id), payload),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INSTANCES_KEY })
  })
}

export function useDeleteEpisode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ instanceId, episodeId }: { instanceId: number; episodeId: number }) =>
      apiClient.delete(API_ENDPOINTS.USER_INSTANCES.EPISODE(instanceId, episodeId)),
    retry: false,
    onSuccess: (_, { instanceId }) =>
      queryClient.invalidateQueries({ queryKey: [...INSTANCES_KEY, 'episodes', instanceId] })
  })
}

export function useUpdateEpisodeContent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      instanceId,
      episodeId,
      payload
    }: {
      instanceId: number
      episodeId: number
      payload: Record<string, unknown>
    }) => apiClient.put(API_ENDPOINTS.USER_INSTANCES.EPISODE_CONTENT(instanceId, episodeId), payload),
    retry: false,
    onSuccess: (_, { instanceId }) =>
      queryClient.invalidateQueries({ queryKey: [...INSTANCES_KEY, 'episodes', instanceId] })
  })
}
